import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import TableHeaders from "./TableHeaders";
import TableBody from "./TableBody";
import TableControls from "./TableControls";
import { withRouter } from "react-router";
import { useSelector } from "react-redux";
import Spinner from "./Spinner/Spinner";
import getCustomerOrders from '../api/getCustomerOrdersList';
import { enqueueSnackbar } from "notistack";
import getOrderItems from "../api/getOrderItems";
import { exportToMARC21 } from "../utils/marc21";
import OverTableButtons from "../components/OverTableButtons";
import Button from "../components/Button";
import PlainContainer from "./PlainContainer";
// Add state for export functionality

const OrdersTable = ({ history }) => {
  const col = [
    {
      Header: "ID",
      accessor: "id",
      columnType: "col-sm-1",
    },
    {
      Header: "Nr faktury",
      accessor: "orderNumber",
      columnType: "col-sm-2",
    },
    {
      Header: "Data zamówienia",
      accessor: "orderDate",
      columnType: "col-sm-2",
      Cell: ({ value }) => {
        const date = new Date(value);
        return date.toLocaleDateString("pl-PL", {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      },
    },
    {
      Header: "Status",
      accessor: "status",
      columnType: "col-sm-2",
    },
    {
      Header: "Kwota",
      accessor: "totalAmount",
      columnType: "col-sm-1",
      Cell: ({ value }) => {
        return `${(value / 100).toFixed(2)} zł`;
      },
    }
  ];

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const loggedUser = useSelector((state) => state.authentication.user);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [filename, setFilename] = useState('');

  const [isExporting, setIsExporting] = useState(false);

  const getOrders = async () => {
    let response;
    if (loggedUser.business_type === "library") {
      response = await getCustomerOrders(loggedUser.libAddress);
      if (!response) {
        enqueueSnackbar("Nie udało się pobrać zamówień", { variant: "error" });
        setLoading(false);
        return;
      }
      if (response.length === 0) {
        enqueueSnackbar("Brak zamówień", { variant: "info" });
        setLoading(false);
        return;
      }
      if (response.error) {
        enqueueSnackbar(`${response.error} [${response.statusCode}]`, { variant: "error" });
        setLoading(false);
        return;
      }
    }

    setOrders(response);
    setLoading(false);
  };

  useEffect(() => {
    getOrders();
  }, []);

  const columns = useMemo(() => col, []);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    pageOptions,
    state,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    prepareRow,
    gotoPage,
    pageCount,
  } = useTable(
    { columns, data: orders, initialState: { pageSize: 18 }, autoResetSortBy: false },
    useSortBy,
    usePagination
  );
  const { pageIndex } = state;

  const orderClicked = async (orderId) => {
    const orderDate = orders.find(order => order.id === orderId)?.orderDate;
    if (selectedOrderId === orderId) {
      // If clicking the same order again, collapse the details
      setSelectedOrderId(null);
      setOrderItems([]);
      setFilename(''); // Clear filename
      return;
    }

    setSelectedOrderId(orderId);
    setLoadingItems(true);

    // Set default filename with timestamp
    const timestamp = new Date(orderDate).toISOString().split('T')[0]; // Format date as YYYY-MM-DD
    setFilename(`zamowienie_${orderId}_(${timestamp})`);

    try {
      // You'll need to create this API function
      const items = await getOrderItems(orderId);
      setOrderItems(items);
    } catch (error) {
      enqueueSnackbar("Nie udało się pobrać szczegółów zamówienia", { variant: "error" });
      setOrderItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  // Update the viewOrder function
  const viewOrder = (id) => {
    // This now happens in orderClicked

    // find the full order in the `orders` array by the selected ID
    const order = orders.find(o => o.id === id)
    if (order) {
      orderDetails(order)
    }

  };


  const exportFiles = async () => {
    if (!orderItems || orderItems.length === 0) {
      enqueueSnackbar("Brak pozycji do eksportu", { variant: "warning" });
      return;
    }
    try {
      setIsExporting(true);
      exportToMARC21(orderItems, selectedOrderId, filename, "Storybox.pl", "Biblioteka - przy T.Kościuszki 49");
      enqueueSnackbar(`Wyeksportowano ${orderItems.length} rekordów MARC21 (.txt i .mrc)`, { variant: "success" });
    } catch (error) {
      console.error('Error exporting MARC21:', error);
      enqueueSnackbar("Błąd podczas eksportu MARC21", { variant: "error" });
    } finally {
      setIsExporting(false);
    }
  }

  const orderDetails = (order) => {
    history.push({
      pathname: `${process.env.PUBLIC_URL}/order/${order.id}`,
      state: { order },
      history: history
    });
  };

  return (
    <>
      <PlainContainer
        heightInVh={4.5}
        widthInVw={0}
        customStyles={{
          top: "-8vh",
          width: "95vw",
          position: "absolute",
          left: "0vw",
          paddingBottom: "0px",
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: "space-between",
          boxShadow: "none",
        }}
      >

      </PlainContainer >
      <div
        className="container"
        {...getTableProps()}
        style={{ maxWidth: "100%", maxHeight: "100%" }}
      >
        {!loading ?
          <>
            <TableHeaders headerGroups={headerGroups} />
            <TableBody
              {...getTableBodyProps()}
              rows={page}
              prepareRow={prepareRow}
              onClick={viewOrder}
              maxHeight={selectedOrderId ? "40vh" : "67vh"} // Adjust height when details are shown
              rowClassName={(row) => selectedOrderId === row.original.id ? "selected-row" : ""}
            />
            <TableControls
              pageOptions={pageOptions}
              nextPage={nextPage}
              previousPage={previousPage}
              canNextPage={canNextPage}
              canPreviousPage={canPreviousPage}
              pageIndex={pageIndex}
              gotoPage={gotoPage}
              pageCount={pageCount}
            />
          </>
          : <div style={{ textAlign: "-webkit-center" }}>
            <Spinner customStyles={{ fontSize: "30px", margin: "40px" }} />
          </div>
        }
      </div>
    </>
  );
};


export default withRouter(OrdersTable);
